import { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Loader2, Check, Send, Settings as Cog, Eye, EyeOff } from "lucide-react";

/**
 * Email Delivery settings card. Shown inside Settings page for Tenant Admin+.
 * - Toggles for the 3 email types (invite, stage-complete, reminder)
 * - Optional per-tenant SendGrid override (key + from email + from name)
 * - Branded template toggle (uses the tenant logo already on file)
 * - "Send test email" validator so recruiters can confirm config before relying on it
 */
export default function EmailDeliveryCard() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [showKey, setShowKey] = useState(false);

  const load = () => api.get("/tenant/email").then((r) => {
    setCfg(r.data);
    setDraft({
      invite: r.data.prefs.invite,
      stage_complete: r.data.prefs.stage_complete,
      reminder: r.data.prefs.reminder,
      branded: r.data.branded,
      sendgrid_api_key: "", // never populate (server never returns it)
      from_email: r.data.using_platform_key ? "" : r.data.from_email,
      from_name: r.data.using_platform_key ? "" : r.data.from_name,
    });
  });
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        prefs: { invite: !!draft.invite, stage_complete: !!draft.stage_complete, reminder: !!draft.reminder },
        branded: !!draft.branded,
      };
      // Only send the key/from if the user actually typed something — otherwise
      // we'd blank out a previously stored custom key.
      if (draft.sendgrid_api_key !== undefined && draft.sendgrid_api_key !== "") {
        payload.sendgrid_api_key = draft.sendgrid_api_key;
      }
      if (draft.from_email !== undefined) payload.from_email = draft.from_email;
      if (draft.from_name !== undefined) payload.from_name = draft.from_name;
      await api.post("/tenant/email", payload);
      toast.success("Email settings saved");
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo || !testTo.includes("@")) { toast.error("Enter a recipient email"); return; }
    setTesting(true);
    try {
      await api.post("/tenant/email/test", { to: testTo });
      toast.success(`Test email sent to ${testTo}. Check inbox + spam.`);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail) || "Test send failed"); }
    finally { setTesting(false); }
  };

  const clearCustomKey = async () => {
    if (!window.confirm("Switch back to the shared RecruitSift SendGrid account?")) return;
    try {
      await api.post("/tenant/email", { sendgrid_api_key: "", from_email: "", from_name: "" });
      toast.success("Switched to RecruitSift default sender");
      load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  if (!cfg) return <div className="tp-card p-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading email settings…</div>;

  return (
    <div className="tp-card p-6" data-testid="email-delivery-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-display text-lg font-semibold flex items-center gap-2"><Mail className="w-5 h-5 text-indigo-600" /> Email Delivery</div>
          <div className="text-xs text-slate-400">Transactional emails to candidates (invites, confirmations). Powered by SendGrid.</div>
        </div>
        <span className="tp-chip tp-chip-emerald text-[11px]"><Check className="w-3 h-3 inline mr-0.5" /> Configured</span>
      </div>

      <div className="mb-5">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Which emails to send?</div>
        <div className="space-y-2">
          <Toggle label="Invite email when a candidate is added" sub="Fires automatically on candidate creation. You can also resend manually from the candidate page." checked={!!draft.invite} onChange={(v) => setDraft({ ...draft, invite: v })} testid="pref-invite" />
          <Toggle label="Confirmation after each stage completes" sub="A brief 'we received your interview' email after telephonic, video, or coding is finished." checked={!!draft.stage_complete} onChange={(v) => setDraft({ ...draft, stage_complete: v })} testid="pref-stage" />
          <Toggle label="48h reminder if the candidate hasn't started" sub="Coming soon — requires a scheduled job. Toggle is persisted but not yet active." checked={!!draft.reminder} onChange={(v) => setDraft({ ...draft, reminder: v })} testid="pref-reminder" disabled />
        </div>
      </div>

      <div className="mb-5 flex items-center gap-3">
        <input type="checkbox" id="branded" className="w-4 h-4 rounded border-slate-300" checked={!!draft.branded} onChange={(e) => setDraft({ ...draft, branded: e.target.checked })} data-testid="pref-branded" />
        <label htmlFor="branded" className="text-sm">
          <span className="font-medium">Tenant-branded template</span>
          <span className="text-slate-500"> — use your uploaded logo in the email header (otherwise RecruitSift mark).</span>
        </label>
      </div>

      <div className="mb-5 p-4 rounded-lg bg-slate-50 border border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-slate-500">Sender</div>
          {cfg.using_platform_key ? (
            <span className="tp-chip text-[11px]">RecruitSift shared account</span>
          ) : (
            <button onClick={clearCustomKey} className="text-[11px] text-slate-500 hover:text-slate-900 underline">Reset to RecruitSift default</button>
          )}
        </div>
        {cfg.using_platform_key ? (
          <div className="text-sm text-slate-600">Emails will come <strong>from</strong> <code className="text-slate-900">{cfg.from_email}</code> on your behalf. Add your own SendGrid key below to use a custom domain.</div>
        ) : (
          <div className="text-sm text-slate-600">Using your SendGrid account. Key: <code className="text-slate-900">{cfg.custom_key_masked}</code> · From: <code className="text-slate-900">{cfg.from_email}</code></div>
        )}

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Custom SendGrid API Key (optional)</label>
            <div className="relative">
              <input
                data-testid="custom-sendgrid-key"
                type={showKey ? "text" : "password"}
                value={draft.sendgrid_api_key || ""}
                onChange={(e) => setDraft({ ...draft, sendgrid_api_key: e.target.value })}
                placeholder={cfg.using_platform_key ? "SG.xxxxxx — leave blank to keep using RecruitSift's shared account" : `${cfg.custom_key_masked} — paste a new key to replace`}
                className="w-full mt-1 px-3 py-2 pr-10 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none text-sm"
              />
              <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-900">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">From Email (optional)</label>
            <input data-testid="custom-from-email" type="email" value={draft.from_email || ""} onChange={(e) => setDraft({ ...draft, from_email: e.target.value })} placeholder="invites@yourdomain.com" className="w-full mt-1 px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">From Name (optional)</label>
            <input data-testid="custom-from-name" value={draft.from_name || ""} onChange={(e) => setDraft({ ...draft, from_name: e.target.value })} placeholder="Your Hiring Team" className="w-full mt-1 px-3 py-2 rounded-lg bg-white border border-slate-300 focus:border-indigo-500 outline-none text-sm" />
          </div>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">The From email must be a <strong>verified sender</strong> in your SendGrid account, otherwise mail will bounce.</div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <input data-testid="test-email-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="your@email.com" type="email" className="px-3 py-2 rounded-lg bg-white border border-slate-300 text-sm" />
          <button data-testid="send-test-email" onClick={sendTest} disabled={testing} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-500 text-indigo-700 hover:bg-indigo-50 text-sm disabled:opacity-60">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
          </button>
        </div>
        <button data-testid="save-email-settings" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cog className="w-4 h-4" />} Save settings
        </button>
      </div>
    </div>
  );
}

function Toggle({ label, sub, checked, onChange, testid, disabled }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${disabled ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white hover:border-indigo-300"}`}>
      <input type="checkbox" data-testid={testid} className="mt-1 w-4 h-4 rounded border-slate-300" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-slate-500">{sub}</div>
      </div>
    </label>
  );
}
